// Country-name → ESPN flag URL resolver. ESPN hosts flags at:
//   https://a.espncdn.com/i/teamlogos/countries/500/{code}.png
// where {code} is the lowercase 3-letter FIFA country code (e.g. esp for Spain,
// usa for United States, eng for England — distinct from ISO 3166 for the
// home nations and a handful of other FIFA-specific exceptions).
//
// Used by the World Cup Bracket UI where team_top / team_bottom strings on
// bracket_template_matchups are country names, and the renderer wants to show
// a flag instead of just text.

const ESPN_FLAG_BASE = 'https://a.espncdn.com/i/teamlogos/countries/500/'

// FIFA country codes for every nation likely to feature in (or qualify for)
// the 2026 World Cup, plus historically-relevant FIFA members. Keys are the
// display names ESPN uses; values are the lowercase code in the flag URL.
const FIFA_CODE = {
  // CONCACAF — hosts + likely qualifiers
  'United States': 'usa',
  'USA': 'usa',
  'Canada': 'can',
  'Mexico': 'mex',
  'Costa Rica': 'crc',
  'Panama': 'pan',
  'Jamaica': 'jam',
  'Honduras': 'hon',
  'El Salvador': 'slv',
  'Guatemala': 'gua',
  'Haiti': 'hai',
  'Trinidad and Tobago': 'tri',
  'Cuba': 'cub',

  // CONMEBOL
  'Argentina': 'arg',
  'Brazil': 'bra',
  'Uruguay': 'uru',
  'Colombia': 'col',
  'Peru': 'per',
  'Chile': 'chi',
  'Ecuador': 'ecu',
  'Paraguay': 'par',
  'Bolivia': 'bol',
  'Venezuela': 'ven',

  // UEFA
  'Spain': 'esp',
  'France': 'fra',
  'Germany': 'ger',
  'England': 'eng',
  'Italy': 'ita',
  'Portugal': 'por',
  'Netherlands': 'ned',
  'Belgium': 'bel',
  'Croatia': 'cro',
  'Switzerland': 'sui',
  'Denmark': 'den',
  'Poland': 'pol',
  'Sweden': 'swe',
  'Serbia': 'srb',
  'Austria': 'aut',
  'Wales': 'wal',
  'Scotland': 'sco',
  'Northern Ireland': 'nir',
  'Republic of Ireland': 'irl',
  'Ireland': 'irl',
  'Czech Republic': 'cze',
  'Czechia': 'cze',
  'Hungary': 'hun',
  'Norway': 'nor',
  'Slovakia': 'svk',
  'Slovenia': 'svn',
  'Ukraine': 'ukr',
  'Russia': 'rus',
  'Romania': 'rou',
  'Turkey': 'tur',
  'Türkiye': 'tur',
  'Greece': 'gre',
  'Iceland': 'isl',
  'Finland': 'fin',
  'Albania': 'alb',
  'Bosnia and Herzegovina': 'bih',
  'Bulgaria': 'bul',
  'Cyprus': 'cyp',
  'Estonia': 'est',
  'Latvia': 'lva',
  'Lithuania': 'ltu',
  'Luxembourg': 'lux',
  'Malta': 'mlt',
  'Moldova': 'mda',
  'Montenegro': 'mne',
  'North Macedonia': 'mkd',
  'Belarus': 'blr',
  'Georgia': 'geo',
  'Armenia': 'arm',
  'Azerbaijan': 'aze',
  'Israel': 'isr',
  'Kosovo': 'kos',
  'Liechtenstein': 'lie',
  'Andorra': 'and',
  'San Marino': 'smr',
  'Gibraltar': 'gib',
  'Faroe Islands': 'fro',

  // CAF
  'Senegal': 'sen',
  'Morocco': 'mar',
  'Tunisia': 'tun',
  'Egypt': 'egy',
  'Algeria': 'alg',
  'Cameroon': 'cmr',
  'Ghana': 'gha',
  'Nigeria': 'nga',
  'Côte d’Ivoire': 'civ',
  "Cote d'Ivoire": 'civ',
  'Ivory Coast': 'civ',
  'South Africa': 'rsa',
  'Mali': 'mli',
  'Burkina Faso': 'bfa',
  'DR Congo': 'cod',
  'Democratic Republic of the Congo': 'cod',
  'Congo': 'cgo',
  'Republic of the Congo': 'cgo',
  'Cape Verde': 'cpv',
  'Cabo Verde': 'cpv',
  'Gabon': 'gab',
  'Guinea': 'gui',
  'Equatorial Guinea': 'eqg',
  'Mozambique': 'moz',
  'Zambia': 'zam',
  'Zimbabwe': 'zim',
  'Uganda': 'uga',
  'Tanzania': 'tan',
  'Kenya': 'ken',
  'Angola': 'ang',
  'Madagascar': 'mad',
  'Mauritania': 'mtn',
  'Sudan': 'sdn',
  'Libya': 'lby',
  'Benin': 'ben',
  'Togo': 'tog',
  'Niger': 'nig',
  'Chad': 'cha',
  'Comoros': 'com',
  'Guinea-Bissau': 'gnb',
  'Sierra Leone': 'sle',
  'Liberia': 'lbr',
  'Namibia': 'nam',
  'Botswana': 'bot',
  'Lesotho': 'les',
  'Eswatini': 'swz',
  'Malawi': 'mwi',
  'Burundi': 'bdi',
  'Rwanda': 'rwa',
  'Ethiopia': 'eth',
  'Somalia': 'som',
  'Djibouti': 'dji',
  'Eritrea': 'eri',
  'Central African Republic': 'caf',
  'São Tomé and Príncipe': 'stp',
  'Sao Tome and Principe': 'stp',
  'Seychelles': 'sey',
  'Mauritius': 'mri',
  'Gambia': 'gam',

  // AFC
  'Japan': 'jpn',
  'South Korea': 'kor',
  'Korea Republic': 'kor',
  'Iran': 'irn',
  'IR Iran': 'irn',
  'Saudi Arabia': 'ksa',
  'Australia': 'aus',
  'Qatar': 'qat',
  'United Arab Emirates': 'uae',
  'UAE': 'uae',
  'Iraq': 'irq',
  'Jordan': 'jor',
  'Lebanon': 'lbn',
  'Syria': 'syr',
  'Bahrain': 'brn',
  'Kuwait': 'kuw',
  'Oman': 'oma',
  'Yemen': 'yem',
  'Palestine': 'ple',
  'Uzbekistan': 'uzb',
  'Tajikistan': 'tjk',
  'Turkmenistan': 'tkm',
  'Kyrgyzstan': 'kgz',
  'Kazakhstan': 'kaz',
  'China': 'chn',
  'China PR': 'chn',
  'Hong Kong': 'hkg',
  'Chinese Taipei': 'tpe',
  'Taiwan': 'tpe',
  'Mongolia': 'mng',
  'North Korea': 'prk',
  'Korea DPR': 'prk',
  'Vietnam': 'vie',
  'Thailand': 'tha',
  'Indonesia': 'idn',
  'Malaysia': 'mas',
  'Singapore': 'sgp',
  'Philippines': 'phi',
  'Myanmar': 'mya',
  'Cambodia': 'cam',
  'Laos': 'lao',
  'Brunei': 'bru',
  'Timor-Leste': 'tls',
  'India': 'ind',
  'Pakistan': 'pak',
  'Bangladesh': 'ban',
  'Sri Lanka': 'sri',
  'Nepal': 'nep',
  'Bhutan': 'bhu',
  'Maldives': 'mdv',
  'Afghanistan': 'afg',

  // OFC
  'New Zealand': 'nzl',
  'Fiji': 'fij',
  'Papua New Guinea': 'png',
  'Solomon Islands': 'sol',
  'Vanuatu': 'van',
  'Tahiti': 'tah',
  'New Caledonia': 'ncl',
  'Samoa': 'sam',
  'American Samoa': 'asa',
  'Cook Islands': 'cok',
  'Tonga': 'tga',
}

/**
 * Resolve a country name to the ESPN flag URL. Returns null if the country
 * isn't in our map — caller should render a fallback (e.g. country initials).
 */
export function getCountryFlagUrl(countryName) {
  if (!countryName) return null
  const code = FIFA_CODE[countryName] || FIFA_CODE[countryName.trim()]
  if (!code) return null
  return `${ESPN_FLAG_BASE}${code}.png`
}

/**
 * Whether a given country name is recognized. Useful for the admin team-entry
 * UI to flag a typo before the template gets saved.
 */
export function isKnownCountry(countryName) {
  if (!countryName) return false
  return !!FIFA_CODE[countryName] || !!FIFA_CODE[countryName.trim()]
}
